import { itemsApi } from "#/api/itemsApi";
import { itemTemplateApi } from "#/api/itemTemplateApi";
import { productApi } from "#/api/productApi";
import { rfidTagApi } from "#/api/rfidTagApi";
import { sitesApi } from "#/api/sitesApi";
import ActionButton from "#/components/buttons/ActionButton";
import DetailPageLayout from "#/components/ui/DetailPageLayout";
import {
  type CreateItemPayload,
  ItemStatus,
  ItemType,
  MaintenanceStatus,
  RetrievalStatus,
} from "#/types/items";
import { getRandomColor } from "#/utils/ColorUtils";
import { sanitize, sanitizeItemStatusLabel } from "#/utils/StringUtils";
import {
  Accordion,
  ActionIcon,
  Badge,
  Button,
  Checkbox,
  CloseButton,
  Divider,
  Drawer,
  Modal,
  MultiSelect,
  SegmentedControl,
  Select,
  TextInput,
  Textarea,
  Title,
  Tooltip,
} from "@mantine/core";
import { DateInput } from "@mantine/dates";
import { useForm } from "@mantine/form";
import { useDebouncedValue, useDisclosure } from "@mantine/hooks";
import { IconExternalLink, IconEye, IconHelp, IconPlus, IconTrash } from "@tabler/icons-react";
import { useMutation, useQuery } from "@tanstack/react-query";
import moment from "moment";

import {
  showErrorNotification,
  showSuccessNotification,
} from "#/components/notifications";
import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import HistoricalDataTable from "./components/HistoricalDataTable";
import { usePermission } from "#/hooks/usePermission";
import PermissionTooltip from "#/components/ui/PermissionTooltip";
import { useAllowedStatuses } from "#/hooks/useAllowedStatuses";

const ItemDetailPage = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { id } = useParams();
  const { canWrite, canDelete } = usePermission();
  const allowedStatuses = useAllowedStatuses();
  const [searchParams] = useSearchParams();

  const [type, setType] = useState<string>(t("items.scan"));

  const [
    showHistoricalDrawer,
    { open: openHistoricalDrawer, close: closeHistoricalDrawer },
  ] = useDisclosure(false);

  const [
    templateModalOpened,
    { open: openTemplateModal, close: closeTemplateModal },
  ] = useDisclosure(false);

  const [templateName, setTemplateName] = useState<string>("");
  const [templateDescription, setTemplateDescription] = useState<string>("");

  const emptyForm = {
    rfid_ids: [],
    product_id: undefined,
    site_id: null,
    date_of_circulation: null,
    notify_email: "",
    external_id: "",
    next_maintenance_date: null,
    last_maintenance_date: null,
    location: "",
    customize_columns: {},
    status: ItemStatus.IN_STOCK_MAIN,
    maintenance_status: MaintenanceStatus.ON_TIME,
    retrieval_status: RetrievalStatus.ON_TIME,
    stolen: false,
    type: ItemType.ITEM,
    cage_item_ids: [],
  };

  const form = useForm<CreateItemPayload>({
    initialValues: emptyForm,
    validate: {
      notify_email: (value) => {
        if (value && !/^\S+@\S+\.\S+$/.test(value)) {
          return "Invalid email";
        }
        return null;
      },
    },
    transformValues: (values) => ({
      ...values,
      date_of_circulation: values.date_of_circulation
        ? moment(values.date_of_circulation).format("YYYY-MM-DD")
        : "",
      next_maintenance_date: values.next_maintenance_date
        ? moment(values.next_maintenance_date).format("YYYY-MM-DD")
        : "",
      last_maintenance_date: values.last_maintenance_date
        ? moment(values.last_maintenance_date).format("YYYY-MM-DD")
        : "",
    }),
  });

  const { data: { products = [] } = {} } = useQuery({
    queryKey: ["products"],
    queryFn: async () => {
      const response = await productApi.getAll({
        page: 1,
        limit: 10000,
      });
      if (!response.data) {
        throw new Error("Failed to fetch categories");
      }
      return {
        products: response.data.data,
      };
    },
    enabled: true,
    staleTime: 1000 * 60,
  });

  const { data: { sites = [] } = {} } = useQuery({
    queryKey: ["sites"],
    queryFn: async () => {
      const response = await sitesApi.getAll({ page: 1, limit: 10000 });
      if (!response.data) {
        throw new Error("Failed to fetch sites");
      }
      return {
        sites: response.data.data,
      };
    },
    enabled: true,
    staleTime: 1000 * 60,
  });


  const { data: { statuses = [] } = {} } = useQuery({
    queryKey: ["product-statuses"],
    queryFn: async () => {
      if (!form.values.product_id) {
        return {
          statuses: [],
        };
      }
      const response = await productApi.getStatuses(
        Number(form.values.product_id)
      );
      if (!response.data) {
        throw new Error("Failed to fetch sites");
      }
      return {
        statuses: response.data,
      };
    },
    enabled: form.values.product_id !== undefined,
    staleTime: 1000 * 60,
  });

  const templateId = searchParams.get("template");

  const templateQuery = useQuery({
    queryKey: ["template-for-item", templateId],
    queryFn: async () => {
      if (!templateId || id !== "new") {
        return;
      }
      const response = await itemTemplateApi.getById(templateId);
      if (!response.data) {
        throw new Error("Failed to fetch template");
      }

      const template = response.data;
      form.setValues({
        rfid_ids: [],
        site_id: template?.site?.id.toString() ?? null,
        product_id: template?.product?.id.toString() ?? undefined,
        date_of_circulation: null,
        notify_email: template.notify_email ?? "",
        external_id: "",
        next_maintenance_date: null,
        last_maintenance_date: null,
        location: template.location ?? "",
        customize_columns: template.customize_columns ?? {},
        status: template.status ?? ItemStatus.IN_STOCK_MAIN,
        maintenance_status: template.maintenance_status ?? MaintenanceStatus.ON_TIME,
        retrieval_status: template.retrieval_status ?? RetrievalStatus.ON_TIME,
        stolen: false,
        type: (template.type as ItemType) ?? ItemType.ITEM,
        cage_item_ids: [],
      });
      form.resetDirty();

      return response.data;
    },
    enabled: id === "new" && templateId !== null,
  });

  const itemQuery = useQuery({
    queryKey: ["item-details", id],
    queryFn: async () => {
      if (!id || id === "new") {
        return;
      }
      const response = await itemsApi.getById(id);
      if (!response.data) {
        throw new Error("Failed to fetch item");
      }

      const item = response.data;
      setSelectedRfidTags(
        (item.rfid_tags ?? []).map((tag) => ({ id: tag.id, rfid: tag.rfid }))
      );
      form.setValues({
        rfid_ids: item.rfid_tags?.map((tag) => tag.id) ?? [],
        site_id: item?.site?.id.toString(),
        product_id: item?.product?.id.toString(),
        date_of_circulation: new Date(item.date_of_circulation),
        notify_email: item.notify_email,
        external_id: item.external_id ?? "",
        next_maintenance_date: item.next_maintenance_date ? new Date(item.next_maintenance_date) : null,
        last_maintenance_date: item.last_maintenance_date ? new Date(item.last_maintenance_date) : null,
        location: item.location,
        customize_columns: item.customize_columns,
        stolen: item.stolen,
        status: item.status,
        maintenance_status: item.maintenance_status,
        retrieval_status: item.retrieval_status,
        type: item.type ?? ItemType.ITEM,
        cage_item_ids: item.cage_items?.map((ci) => ci.id) ?? [],
      });
      form.resetDirty();

      return response.data;
    },
    enabled: id !== undefined && id !== "new",
  });

  const [rfidTagSearch, setRfidTagSearch] = useState("");
  const [debouncedRfidTagSearch] = useDebouncedValue(rfidTagSearch, 400);
  const [selectedRfidTags, setSelectedRfidTags] = useState<{ id: number; rfid: string }[]>([]);

  const { data: { searchResults = [] } = {}, isLoading: rfidTagsLoading } = useQuery({
    queryKey: ["rfid-tags-search", debouncedRfidTagSearch],
    queryFn: async () => {
      const response = await rfidTagApi.getAll({
        status: "not_linked",
        limit: 20,
        search: debouncedRfidTagSearch,
      });
      return { searchResults: response.data.data || [] };
    },
    enabled: debouncedRfidTagSearch.length >= 2,
  });

  const rfidTagOptions = useMemo(() => {
    const selectedIds = new Set(selectedRfidTags.map((t) => t.id));
    const fromSearch = searchResults.filter((t) => !selectedIds.has(t.id));
    return [...selectedRfidTags, ...fromSearch].map((t) => ({
      label: t.rfid,
      value: t.id.toString(),
    }));
  }, [selectedRfidTags, searchResults]);

  const [cageItemSearch, setCageItemSearch] = useState<string>("");
  const [debouncedCageItemSearch] = useDebouncedValue(cageItemSearch, 400);

  const {
    data: { cageItems: availableCageItems = [] } = {},
    isLoading: cageItemsLoading,
    refetch: refetchCageItems,
  } = useQuery({
    queryKey: ["available-cage-items", id, debouncedCageItemSearch, itemQuery.data?.id],
    queryFn: async () => {
      const response = await itemsApi.getAvailableCageItems({
        search: debouncedCageItemSearch || undefined,
        cage_id: id !== "new" ? Number(id) : undefined,
        page: 1,
        limit: 10000,
      });

      return { cageItems: response.data.data || [] };
    },
    enabled: form.values.type === ItemType.CAGE,
    staleTime: 1000 * 60,
  });

  const inlineCageItemMutation = useMutation({
    mutationFn: async (externalId: string) => {
      const productId = form.values.product_id
        ? Number(form.values.product_id)
        : undefined;
      if (!productId) {
        throw new Error(t("items.inlineCageItem.selectCategoryFirst"));
      }
      return await itemsApi.create({
        external_id: externalId,
        product_id: productId,
        type: ItemType.CAGE_ITEM,
      });
    },
    onSuccess: (data) => {
      const newItem = data.data;
      const currentIds = form.values.cage_item_ids ?? [];
      form.setFieldValue("cage_item_ids", [...currentIds, newItem.id]);
      setCageItemSearch("");
      void refetchCageItems();
      showSuccessNotification({
        message: t("items.inlineCageItem.createdSuccess", { externalId: newItem.external_id }),
      });
    },
    onError: (error) => {
      showErrorNotification({
        message: error.message ?? t("items.inlineCageItem.createdError"),
      });
    },
  });

  // Helper function to filter out empty values
  const filterEmptyValues = (values: typeof form.values): Partial<CreateItemPayload> => {
    const filteredPayload: Partial<CreateItemPayload> = {};

    (Object.entries(values) as [keyof CreateItemPayload, CreateItemPayload[keyof CreateItemPayload]][]).forEach(([key, value]) => {
      // Skip empty strings, empty arrays, null, undefined
      if (value === "" || value === null || value === undefined) {
        return;
      }
      if (Array.isArray(value) && value.length === 0) {
        return;
      }
      // Skip empty objects
      if (typeof value === "object" && !Array.isArray(value) && Object.keys(value as Record<string, unknown>).length === 0) {
        return;
      }

      // Convert site_id and product_id to numbers if they exist
      if (key === "site_id" || key === "product_id") {
        filteredPayload[key] = Number(value);
      } else {
        (filteredPayload as Record<string, CreateItemPayload[keyof CreateItemPayload]>)[key as string] = value;
      }
    });

    return filteredPayload;
  };

  const handleSubmit = (values: typeof form.values) => {
    const filteredPayload = filterEmptyValues(values);
    itemMutation.mutate(filteredPayload as CreateItemPayload);
  };

  const itemMutation = useMutation({
    mutationFn: async (payload: CreateItemPayload) => {
      if (id !== "new") {
        return await itemsApi.update(Number(id), payload);
      } else {
        return await itemsApi.create(payload);
      }
    },
    onSuccess: (data) => {
      if (id === "new") {
        void navigate(`/inventory/items/${data.data.id}`);
      }
      showSuccessNotification({
        message: `Item ${id === "new" ? "created" : "updated"} successfully`,
      });
    },
    onError: (error) => {
      showErrorNotification({
        message: error.message ?? "Failed to process. Please try again later",
      });
    },
  });

  const createAndSaveAsTemplateMutation = useMutation({
    mutationFn: async ({
      itemPayload,
      templateName,
      templateDescription,
    }: {
      itemPayload: CreateItemPayload;
      templateName: string;
      templateDescription: string;
    }) => {
      const itemResponse = await itemsApi.create(itemPayload);

      const templatePayload = {
        name: templateName,
        description: templateDescription || undefined,
        product_id: itemPayload.product_id ? Number(itemPayload.product_id) : null,
        site_id: itemPayload.site_id ? Number(itemPayload.site_id) : null,
        location: itemPayload.location || undefined,
        notify_email: itemPayload.notify_email || undefined,
        status: itemPayload.status || undefined,
        retrieval_status: itemPayload.retrieval_status || undefined,
        maintenance_status: itemPayload.maintenance_status || undefined,
        customize_columns: itemPayload.customize_columns || undefined,
        type: itemPayload.type || undefined,
      };

      await itemTemplateApi.create(templatePayload);

      return itemResponse;
    },
    onSuccess: (data) => {
      void navigate(`/inventory/items/${data.data.id}`);
      showSuccessNotification({
        message: "Item created and saved as template successfully",
      });
      closeTemplateModal();
    },
    onError: (error) => {
      showErrorNotification({
        message: error.message ?? "Failed to process. Please try again later",
      });
    },
  });

  return (
    <DetailPageLayout
      title={id != "new" ? t("items.itemDetails") : t("items.createItem")}
      loading={itemMutation.isPending || itemQuery.isLoading || templateQuery.isLoading}
      navRightSection={
        <div className={`w-full flex gap-2 items-center`}>
          <Button
            color="black"
            variant="outline"
            type="reset"
            disabled={!form.isDirty()}
            onClick={() => {
              form.reset();
            }}
            size="xs"
          >
            {t("common.clear")}
          </Button>

          <PermissionTooltip disabled={!canWrite()}>
            <Button
              color="black"
              variant="filled"
              loading={itemMutation.isPending}
              disabled={!form.isDirty() || !canWrite()}
              onClick={() => {
                form.onSubmit(handleSubmit)();
              }}
              size="xs"
            >
              {id === "new" ? t("common.create") : t("common.save")}
            </Button>
          </PermissionTooltip>

          {id === "new" && (
            <PermissionTooltip disabled={!canWrite()}>
              <Button
                color="blue"
                variant="filled"
                disabled={!form.isDirty() || !canWrite()}
                onClick={() => {
                  form.onSubmit(() => {
                    setTemplateName(`Template from item ${new Date().toISOString()}`);
                    setTemplateDescription(`Auto-generated template from item creation`);
                    openTemplateModal();
                  })();
                }}
                size="xs"
              >
                {t("items.createAndSaveAsTemplate")}
              </Button>
            </PermissionTooltip>
          )}

          {id !== "new" && (
            <div className="flex items-center gap-4 pl-2">
              <Divider orientation="vertical" />
              <Button
                variant="outline"
                color="violet"
                leftSection={<IconEye size={16} />}
                onClick={openHistoricalDrawer}
                size="xs"
              >
                {t("common.historicalData")}
              </Button>
            </div>
          )}
        </div>
      }
    >
      <div className="w-full flex flex-col">
        <form
          className="space-y-8 relative"
          onSubmit={form.onSubmit(handleSubmit)}
        >
          <Accordion
            variant={"separated"}
            defaultValue={["basic", "customize_columns", "cage_items"]}
            multiple
          >
            <Accordion.Item value={"basic"}>
              <Accordion.Control>{t("common.basicDetails")}</Accordion.Control>
              <Accordion.Panel>
                <div className="w-full grid grid-cols-1 gap-4">
                  <div className="w-full grid grid-cols-2 gap-4">
                    <Select
                      label={t("items.form.type")}
                      data={[
                        { label: t("items.types.item"), value: ItemType.ITEM },
                        { label: t("items.types.cage"), value: ItemType.CAGE },
                        { label: t("items.types.cageItem"), value: ItemType.CAGE_ITEM },
                      ]}
                      disabled={!canWrite() || itemQuery.isLoading || itemMutation.isPending}
                      {...form.getInputProps("type")}
                      onChange={(value) => {
                        form.setFieldValue("type", (value as ItemType) ?? ItemType.ITEM);
                        if (value !== ItemType.CAGE) {
                          form.setFieldValue("cage_item_ids", []);
                        }
                      }}
                    />

                    <TextInput
                      label={t("items.form.externalId")}
                      disabled={!canWrite() || itemQuery.isLoading || itemMutation.isPending}
                      placeholder={t("items.form.externalIdPlaceholder")}
                      {...form.getInputProps("external_id")}
                    />
                  </div>

                  <MultiSelect
                    label={t("items.form.rfidTags")}
                    placeholder="Type to search RFID tags"
                    searchable
                    searchValue={rfidTagSearch}
                    onSearchChange={setRfidTagSearch}
                    disabled={
                      !canWrite() ||
                      itemQuery.isLoading ||
                      itemMutation.isPending
                    }
                    data={rfidTagOptions}
                    value={form.values.rfid_ids?.map((id) => id.toString()) ?? []}
                    onChange={(values) => {
                      const numericIds = values.map((v) => Number(v));
                      // Update selWHAected tags state from current options
                      const allTags = [...selectedRfidTags, ...searchResults];
                      setSelectedRfidTags(
                        numericIds.map((nid) => {
                          const tag = allTags.find((t) => t.id === nid);
                          return { id: nid, rfid: tag?.rfid ?? nid.toString() };
                        })
                      );
                      form.setFieldValue("rfid_ids", numericIds);
                    }}
                    clearable
                    filter={({ options }) => options}
                    nothingFoundMessage={
                      rfidTagSearch.length < 2
                        ? "Type at least 2 characters to search"
                        : rfidTagsLoading
                          ? "Searching..."
                          : "No RFID tags found"
                    }
                  />

                  <div className="w-full grid grid-cols-2 gap-4">
                    <div className="w-full flex items-center relative">
                      <div className="w-full">
                        <Select
                          label={t("items.form.site")}
                          placeholder={t("items.form.sitePlaceholder")}
                          searchable
                          disabled={
                            !canWrite() ||
                            itemQuery.isLoading || itemMutation.isPending
                          }
                          data={sites.map((site) => {
                            return {
                              label: site?.name ?? "",
                              value: site?.id?.toString() ?? "",
                            };
                          })}
                          {...form.getInputProps("site_id")}
                        />
                      </div>

                      <div className="a absolute top-0 right-0">
                        <Tooltip
                          label="Site is a required field. An item must belong to a site"
                          withArrow
                        >
                          <ActionIcon variant="subtle" color="gray">
                            <IconHelp size={16} />
                          </ActionIcon>
                        </Tooltip>
                      </div>
                    </div>

                    <div className="w-full flex items-center relative">
                      <div className="w-full">
                        <Select
                          label={t("items.form.category")}
                          placeholder={t("items.form.categoryPlaceholder")}
                          searchable
                          disabled={
                            !canWrite() ||
                            itemQuery.isLoading || itemMutation.isPending
                          }
                          data={products.map((prod) => {
                            return {
                              label: prod?.name ?? "",
                              value: prod?.id?.toString() ?? "",
                            };
                          })}
                          clearable
                          {...form.getInputProps("product_id")}
                        />
                      </div>

                      <div className="a absolute top-0 right-0">
                        <Tooltip
                          label="Category is a required field. It is a classification of the item that you are about to create. It contains essential information like type, cost etc."
                          withArrow
                          multiline
                          w={220}
                        >
                          <ActionIcon variant="subtle" color="gray">
                            <IconHelp size={16} />
                          </ActionIcon>
                        </Tooltip>
                      </div>
                    </div>

                  </div>

                  <div className="w-full grid grid-cols-3 gap-4">
                    <DateInput
                      label={t("items.form.dateOfCirculation")}
                      placeholder="Select a date of circulation"
                      disabled={!canWrite() || itemQuery.isLoading || itemMutation.isPending}
                      {...form.getInputProps("date_of_circulation")}
                    />

                    <DateInput
                      label={t("items.form.lastMaintenanceDate")}
                      disabled={!canWrite() || itemQuery.isLoading || itemMutation.isPending}
                      placeholder="Select a last maintenance date"
                      maxDate={new Date()}
                      {...form.getInputProps("last_maintenance_date")}
                    />

                    <DateInput
                      label={t("items.form.nextMaintenanceDate")}
                      disabled={!canWrite() || itemQuery.isLoading || itemMutation.isPending}
                      placeholder="Select a next maintenance date"
                      minDate={new Date()}
                      {...form.getInputProps("next_maintenance_date")}
                    />
                  </div>

                  <div className="w-full grid grid-cols-3 gap-4">
                    <Select
                      label={t("items.form.status")}
                      placeholder={t("items.form.statusPlaceholder")}
                      searchable
                      disabled={!canWrite()}
                      data={[
                        {
                          group: t("common.default"),
                          items: allowedStatuses.map((status) => ({
                            label: sanitizeItemStatusLabel(status),
                            value: status,
                          })),
                        },
                        {
                          group: t("common.categoryStatuses"),
                          items: (() => {
                            const allowedValueSet = new Set<string>(allowedStatuses);
                            const seenLabels = new Set<string>();
                            return statuses
                              .filter((scanStatus) => {
                                const label = scanStatus.label ?? "";
                                if (!label || allowedValueSet.has(label)) {
                                  return false;
                                }
                                if (seenLabels.has(label)) {
                                  return false;
                                }
                                seenLabels.add(label);
                                return true;
                              })
                              .map((scanStatus) => ({
                                label: `Step ${scanStatus.step} : ${scanStatus.label}`,
                                value: scanStatus.label ?? "",
                              }));
                          })(),
                        },
                      ]}
                      clearable
                      {...form.getInputProps("status")}
                    />

                    <Select
                      label={t("items.form.retrievalStatus")}
                      placeholder={t("items.form.statusPlaceholder")}
                      searchable
                      disabled={!canWrite()}
                      data={Object.values(RetrievalStatus).map((status) => ({
                        label: sanitize(status),
                        value: status,
                      }))}
                      clearable
                      {...form.getInputProps("retrieval_status")}
                    />

                    <Select
                      label={t("items.form.maintenanceStatus")}
                      placeholder={t("items.form.statusPlaceholder")}
                      searchable
                      disabled={!canWrite()}
                      data={Object.values(MaintenanceStatus).map((status) => ({
                        label: sanitize(status),
                        value: status,
                      }))}
                      clearable
                      {...form.getInputProps("maintenance_status")}
                    />
                  </div>

                  <div className="w-full grid grid-cols-2 gap-4">
                    <TextInput
                      label={t("items.form.location")}
                      disabled={!canWrite() || itemQuery.isLoading || itemMutation.isPending}
                      placeholder={t("items.form.locationPlaceholder")}
                      {...form.getInputProps("location")}
                    />

                    <TextInput
                      label={t("items.form.manufacturerEmail")}
                      disabled={!canWrite() || itemQuery.isLoading || itemMutation.isPending}
                      placeholder={t("items.form.manufacturerEmailPlaceholder")}
                      type="email"
                      {...form.getInputProps("notify_email")}
                    />
                  </div>
                </div>

                {id !== "new" && (
                  <div className="border border-slate-300 rounded-lg bg-slate-50 p-4 mt-4">
                    <PermissionTooltip disabled={!canWrite()}>
                      <Checkbox
                        label={t("items.form.markAsStolen")}
                        color="red.5"
                        disabled={itemQuery.isLoading || itemMutation.isPending || !canWrite()}
                        checked={form.values.stolen}
                        classNames={{
                          label: "font-bold text-red-500 text-base",
                        }}
                        size="sm"
                        description="This will flag the item as stolen. Next time someone scans or brings it to a filling station, it will notify the client by email and will also display red flag on the filling station display "
                        {...form.getInputProps("stolen")}
                      />
                    </PermissionTooltip>
                  </div>
                )}
              </Accordion.Panel>
            </Accordion.Item>

            {form.values.type === ItemType.CAGE && (
              <Accordion.Item value="cage_items">
                <Accordion.Control>{t("items.cageItems")}</Accordion.Control>
                <Accordion.Panel>
                  <MultiSelect
                    label={t("items.form.cageItems")}
                    placeholder={
                      cageItemsLoading
                        ? t("common.loading")
                        : t("items.form.cageItemsPlaceholder")
                    }
                    searchable
                    hidePickedOptions
                    disabled={
                      !canWrite() ||
                      itemQuery.isLoading ||
                      itemMutation.isPending
                    }
                    data={availableCageItems.map((ci) => ({
                      label: `${ci.external_id || `ID: ${ci.id}`}${ci.rfid_tags && ci.rfid_tags.length > 0 ? ` (${ci.rfid_tags[0].rfid})` : ""}`,
                      value: ci.id.toString(),
                    }))}
                    value={
                      form.values.cage_item_ids?.map((cid) => cid.toString()) ?? []
                    }
                    onChange={(values) => {
                      form.setFieldValue(
                        "cage_item_ids",
                        values.map((v) => Number(v)),
                      );
                    }}
                    onSearchChange={setCageItemSearch}
                    onKeyDown={(e) => {
                      if (
                        e.key === "Enter" &&
                        cageItemSearch &&
                        !availableCageItems.some(
                          (ci) => ci.external_id === cageItemSearch,
                        ) &&
                        form.values.product_id &&
                        !inlineCageItemMutation.isPending
                      ) {
                        e.preventDefault();
                        inlineCageItemMutation.mutate(cageItemSearch);
                      }
                    }}
                    clearable
                    nothingFoundMessage={
                      cageItemSearch ? (
                        <Button
                          variant="light"
                          color="blue"
                          size="xs"
                          fullWidth
                          loading={inlineCageItemMutation.isPending}
                          onClick={() => {
                            inlineCageItemMutation.mutate(cageItemSearch);
                          }}
                        >
                          {t("items.form.createCageItemInline", {
                            externalId: cageItemSearch,
                          })}
                        </Button>
                      ) : (
                        t("items.form.noCageItemsAvailable")
                      )
                    }
                  />
                  {(form.values.cage_item_ids ?? []).length > 0 && (
                    <div className="mt-3">
                      <div className="flex items-center gap-1">
                        <span className="text-xs font-bold text-slate-600">{t("items.currentlyLinkedItems")}</span>
                        <span className="text-xs text-slate-600">{t("items.clickOnItemsToSeeDetails")}</span>
                      </div>
                      <div className="flex flex-wrap gap-1 mt-1">
                        {(form.values.cage_item_ids ?? []).map((cid) => {
                          const ci = availableCageItems.find((c) => c.id === cid);
                          const label = ci
                            ? `${ci.external_id || `ID: ${ci.id}`}${ci.rfid_tags && ci.rfid_tags.length > 0 ? ` (${ci.rfid_tags[0].rfid})` : ""}`
                            : `ID: ${cid}`;
                          return (
                            <Badge
                              key={cid}
                              color={getRandomColor(label)}
                              variant="light"
                              size="lg"
                              className="cursor-pointer hover:opacity-80 transition-opacity"
                              rightSection={<IconExternalLink size={12} className="opacity-60" />}
                              onClick={() => window.open(`/inventory/items/${cid}`, '_blank')}
                            >
                              {label}
                            </Badge>
                          );
                        })}
                      </div>
                    </div>
                  )}
                </Accordion.Panel>
              </Accordion.Item>
            )}

            <Accordion.Item value="customize_columns">
              <Accordion.Control>
                <div className="flex items-center gap-2">
                  <span>{t("common.customFields")}</span>
                  <PermissionTooltip disabled={!canWrite()}>
                    <ActionButton
                      color="black"
                      size="xs"
                      icon={IconPlus}
                      disabled={!canWrite()}
                      onClick={(e) => {
                        e.stopPropagation();
                        const existing = { ...form.values.customize_columns };
                        let newKey = `field_${Object.keys(existing).length + 1}`;
                        while (newKey in existing) {
                          newKey += "_";
                        }
                        form.setFieldValue("customize_columns", {
                          ...existing,
                          [newKey]: "",
                        });
                      }}
                    />
                  </PermissionTooltip>
                </div>
              </Accordion.Control>

              <Accordion.Panel>
                {Object.entries(form.values.customize_columns ?? {}).length <=
                0 ? (
                  <div>{t("common.addCustomFieldHint")}</div>
                ) : (
                  <div className="w-full flex flex-col gap-2">
                    {Object.entries(form.values.customize_columns ?? {}).map(
                      ([key, value], index) => {
                        const fieldId = `${key}_${index}`;

                        return (
                          <div
                            key={fieldId}
                            className="w-full grid grid-cols-[auto_1fr_1fr_auto] gap-2 items-center"
                          >
                            {/* Editable Key input */}
                            <span className="bg-black/5 font-bold text-black/70 rounded-full p-2 w-8 h-8 flex items-center justify-center">
                              <span>{index + 1}</span>
                            </span>
                            <TextInput
                              defaultValue={key}
                              disabled={!canWrite()}
                              onBlur={(event) => {
                                const newKey = event.currentTarget.value.trim();
                                if (!newKey || newKey === key) return;

                                const updated = {
                                  ...form.values.customize_columns,
                                };
                                const val = updated[key];
                                delete updated[key];

                                if (newKey in updated) return; // avoid duplicates

                                updated[newKey] = val;
                                form.setFieldValue(
                                  "customize_columns",
                                  updated
                                );
                              }}
                            />

                            {/* Editable Value input */}
                            <TextInput
                              value={value}
                              disabled={!canWrite()}
                              onChange={(event) => {
                                form.setFieldValue(
                                  `customize_columns.${key}`,
                                  event.currentTarget.value
                                );
                              }}
                            />

                            {/* Delete Button */}
                            <PermissionTooltip disabled={!canDelete()}>
                              <ActionButton
                                icon={IconTrash}
                                variant="white"
                                color="red.5"
                                disabled={!canDelete()}
                                onClick={() => {
                                  const updated = {
                                    ...form.values.customize_columns,
                                  };
                                  delete updated[key];
                                  form.setFieldValue(
                                    "customize_columns",
                                    updated
                                  );
                                }}
                              />
                            </PermissionTooltip>
                          </div>
                        );
                      }
                    )}
                  </div>
                )}
              </Accordion.Panel>
            </Accordion.Item>

            <Drawer
              opened={showHistoricalDrawer}
              onClose={closeHistoricalDrawer}
              position="bottom"
              size={"90%"}
              withCloseButton={false}
            >
              <div className="w-full flex flex-col gap-4">
                <div className="w-full flex items-center justify-between border-b border-b-slate-300 pb-4">
                  <div className="flex items-center gap-2">
                    <Title order={3}>{t("common.historicalData")}</Title>
                    <SegmentedControl
                      data={[t("items.scan"), t("items.refill")]}
                      size="xs"
                      onChange={setType}
                    />
                  </div>
                  <CloseButton onClick={closeHistoricalDrawer} />
                </div>
                {type === t("items.refill") && (
                  <HistoricalDataTable item={itemQuery.data} type="refill" />
                )}

                {type === t("items.scan") && (
                  <HistoricalDataTable item={itemQuery.data} type="scan" />
                )}
              </div>
            </Drawer>
          </Accordion>
        </form>
      </div>

      <Modal
        opened={templateModalOpened}
        onClose={closeTemplateModal}
        centered
        title={t("items.saveAsTemplate")}
        closeOnClickOutside={false}
        classNames={{
          title: "font-bold",
        }}
      >
        <div className="w-full flex flex-col gap-4">
          <TextInput
            label={t("items.templateName")}
            withAsterisk
            placeholder={t("templates.form.templateNamePlaceholder")}
            value={templateName}
            onChange={(e) => setTemplateName(e.currentTarget.value)}
          />

          <Textarea
            label={t("common.description")}
            placeholder={t("templates.form.descriptionPlaceholder")}
            rows={3}
            value={templateDescription}
            onChange={(e) => setTemplateDescription(e.currentTarget.value)}
          />

          <div className="w-full grid grid-cols-2 gap-2 mt-2">
            <Button
              variant="outline"
              color="black"
              onClick={closeTemplateModal}
            >
              {t("common.cancel")}
            </Button>
            <Button
              color="blue"
              disabled={!templateName}
              loading={createAndSaveAsTemplateMutation.isPending}
              onClick={() => {
                const payload = filterEmptyValues(form.values);
                createAndSaveAsTemplateMutation.mutate({
                  itemPayload: payload as CreateItemPayload,
                  templateName,
                  templateDescription,
                });
              }}
            >
              {t("common.confirm")}
            </Button>
          </div>
        </div>
      </Modal>
    </DetailPageLayout>
  );
};

export default ItemDetailPage;
